import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import AdvancedTypography from 'Components/UI/AdvancedTypography';
import { useSearchBarState } from 'Components/AdvisorApp/Header/ClientSearchBar/BetaSearchBar/SearchBarContext';

const styles = () => ({
  root: {
    pointerEvents: 'none',
    userSelect: 'none',
  },
});

const buildFragments = (searchResultResource, attrName, searchTermBeforeExclusion, matchesOn, TypographyProps) => {
  const searchResultMatchesOn = searchResultResource.attr('matchedOn');
  const matchOnItem = matchesOn.find((v) => {
    return v.matchOn === searchResultMatchesOn;
  }) || {};
  const { matchOn, exclude = '' } = matchOnItem;
  // Start by expecting to exclude nothing from the start of the search term
  let excludeCount = 0;
  // Look to see if the search term has an occurrence of the text that can be excluded (IE. '#' in the case of phone number)
  const excludeIndexOf = searchTermBeforeExclusion.lastIndexOf(exclude);
  // If the search term starts with the exclusion (IE. '#' for phone number)
  if (excludeIndexOf === 0) {
    // Then keep track of how many chars we'd like to exclude
    excludeCount = exclude.length;
  }
  // And slice them off the start of our search.
  const searchTerm = searchTermBeforeExclusion.slice(excludeCount);
  const shouldBold = matchOn && searchTerm.length > 0;
  const attrValue = searchResultResource.attr(attrName);
  const attrValueLength = attrValue.length;
  const totalMissFragments = [
    {
      variant: 'h5',
      text: searchResultResource.attr(attrName),
      ...TypographyProps,
    },
  ];

  if (!shouldBold) {
    return totalMissFragments;
  }

  const matchStartChars = ['"', ' '];
  const searchTermLength = searchTerm.length;
  const fragments = [];
  let searchTermIdx = 0; // searchTerm pointer
  let readyToMatch = true;
  let nonMatchIdx = 0;
  for (let i = 0; i < attrValue.length; i++) {
    if (!readyToMatch) {
      if (matchStartChars.includes(attrValue[i])) {
        readyToMatch = true;
      }
    } else if (searchTerm[searchTermIdx].toLowerCase() === attrValue[i].toLowerCase()) {
      searchTermIdx++;
      if (searchTermIdx === searchTermLength) {
        const startOfMatchIdx = i + 1 - searchTermLength;
        // We have fully matched the search term
        fragments.push(
          {
            text: attrValue.slice(nonMatchIdx, startOfMatchIdx),
            length: startOfMatchIdx - nonMatchIdx,
            ...TypographyProps,
          },
          {
            text: attrValue.slice(startOfMatchIdx, i + 1),
            length: i + 1 - startOfMatchIdx,
            ...TypographyProps,
            weight: 'bold',
          },
        );

        searchTermIdx = 0;
        nonMatchIdx = i + 1;
        readyToMatch = false;
      }
    } else {
      searchTermIdx = 0;
      readyToMatch = matchStartChars.includes(attrValue[i]);
    }
  }

  // If no fragments found then no match found
  if (fragments.length === 0) {
    return totalMissFragments;
  }

  fragments.push({
    text: attrValue.slice(nonMatchIdx, attrValueLength),
    length: attrValueLength - nonMatchIdx,
    ...TypographyProps,
  });

  const filteredFragments = fragments.filter((v) => {
    return v.length > 0;
  }).map(({ length, ...rest }) => {
    return rest;
  });

  return filteredFragments;
};

const TextLine = ({
  className,
  classes,
  searchResultResource,
  attrName,
  matchesOn,
  TypographyProps,
}) => {
  const { searchTerm } = useSearchBarState();

  return (
    <AdvancedTypography
      className={classNames(classes.root, className)}
      fragments={buildFragments(searchResultResource, attrName, searchTerm, matchesOn, TypographyProps)}
    />
  );
};

TextLine.defaultProps = {
  className: '',
  TypographyProps: {},
};

TextLine.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  searchResultResource: PropTypes.object.isRequired,
  attrName: PropTypes.string.isRequired,
  matchesOn: PropTypes.arrayOf(PropTypes.object).isRequired,
  TypographyProps: PropTypes.object,
};

export default withStyles(styles)(TextLine);
